import "./CaseInfo.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { MouseEventHandler } from "react";
import { useSelector } from "react-redux";

import { Case } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import ApplicationState from "store/ApplicationState";

import { validOrderingDoctor } from "./dialogs/utils";
import DocsGrid from "./DocsGrid";
import {
  show_clinic,
  show_physician,
} from "./modals/PhysicianPreferencesModal";
import LinkButton from "../LinkButton";
import PatientSexAge from "../PatientSexAge";

interface CaseInfoProps {
  caseDetail: CaseDetailState;
  slideId?: number;
  setPatientModal: (show: boolean) => void;
  setPreferencesModal: (show: boolean) => void;
  setReasonsModal?: (show: boolean) => void;
}

const AorText = ({
  onClick,
  children,
}: {
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children: React.ReactNode;
}) => {
  return onClick ? <a onClick={onClick}>{children}</a> : <>{children}</>;
};

export const ClinicianInfo = (
  props: Omit<CaseInfoProps, "setPatientModal">
) => {
  const { caseDetail, setPreferencesModal } = props;
  return (
    <div className="CaseInfo CaseInfo--preferences">
      <p>
        <AorText
          onClick={
            show_physician(caseDetail)
              ? () => setPreferencesModal(true)
              : undefined
          }
        >
          <strong>
            {caseDetail.ordering_doctor?.first_name}&nbsp;
            {caseDetail.ordering_doctor?.last_name}
          </strong>
          {!validOrderingDoctor(caseDetail.ordering_doctor) && (
            <>
              &nbsp;
              <FontAwesomeIcon
                icon="exclamation-circle"
                color="var(--red)"
                title="Missing Ordering Provider"
              />
            </>
          )}
          {caseDetail.physician_preferences && <span> Ⓟ</span>}
          {validOrderingDoctor(caseDetail.ordering_doctor) && (
            <strong>
              {caseDetail.ordering_doctor?.email && (
                <small>
                  <FontAwesomeIcon icon="envelope" />
                </small>
              )}
              {caseDetail.ordering_doctor?.userprofile?.phone && (
                <small>
                  <FontAwesomeIcon icon="phone" />
                </small>
              )}
            </strong>
          )}
        </AorText>
      </p>
      <p>
        <AorText
          onClick={
            show_clinic(caseDetail)
              ? () => setPreferencesModal(true)
              : undefined
          }
        >
          <strong>{caseDetail.patient?.clinic?.name}</strong>
          {caseDetail.clinic_preferences && <span> Ⓟ</span>}
          <strong>
            {caseDetail.patient.clinic?.email && (
              <small>
                <FontAwesomeIcon icon="envelope" />
              </small>
            )}
            {caseDetail.patient.clinic?.phone && (
              <small>
                <FontAwesomeIcon icon="phone" />
              </small>
            )}
          </strong>
        </AorText>
      </p>
    </div>
  );
};

const CaseInfo = (props: CaseInfoProps) => {
  const {
    caseDetail,
    // eslint-disable-next-line unused-imports/no-unused-vars
    slideId = 0,
    setPreferencesModal,
  } = props;

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const user = useSelector(({ auth }: ApplicationState) => auth.user);

  const hasWorkingReasons =
    caseDetail.working_reasons &&
    Object.keys(caseDetail.working_reasons).length !== 0;

  const availableForReasonsModal =
    caseDetail.status === Case.status.WORKING &&
    hasWorkingReasons &&
    props.setReasonsModal;

  return (
    <>
      <div className="CaseInfo">
        <h1>
          <strong>{caseDetail.accession}</strong>
          {caseDetail.status && (
            <small
              data-testid={"status"}
              onClick={
                availableForReasonsModal
                  ? () => {
                      props.setReasonsModal && props.setReasonsModal(true);
                    }
                  : undefined
              }
              className={classNames("CaseInfo__status", {
                "CaseInfo__status--reasons": availableForReasonsModal,
              })}
            >
              ({caseDetail.status})
              {availableForReasonsModal && (
                <>
                  &nbsp;<strong>ⓘ</strong>
                </>
              )}
            </small>
          )}
        </h1>
        {!features?.mrmcStudy ? (
          <h2 className="CaseInfo__patient">
            <strong
              onClick={() => {
                props.setPatientModal(true);
              }}
            >
              {caseDetail.patient.last_name},&nbsp;
              {caseDetail.patient.first_name}
              <small>
                (
                <PatientSexAge
                  patient={caseDetail.patient}
                  shouldDisplayDOB={false}
                />
                )<LinkButton className="info-icon">ⓘ</LinkButton>{" "}
              </small>
              {features?.patientHistory &&
                !!caseDetail.patient_matches?.length && (
                  <span className="info-icon info-icon--history">Ⓗ</span>
                )}
            </strong>
          </h2>
        ) : (
          <h2 style={{ textAlign: "center" }}>
            <strong>
              <small>
                <PatientSexAge
                  patient={caseDetail.patient}
                  shouldDisplayDOB={false}
                />
              </small>
            </strong>
          </h2>
        )}
      </div>
      {(user?.user_type === "dermpath" || user?.is_superuser) && (
        <ClinicianInfo
          caseDetail={caseDetail}
          setPreferencesModal={setPreferencesModal}
        />
      )}
      {!features?.mrmcStudy &&
        (caseDetail.documents?.length > 0 ||
          caseDetail.accession_prefix?.is_diagnostic) && (
          <DocsGrid caseDetail={caseDetail} />
        )}
    </>
  );
};
export default CaseInfo;
